import { useRef, useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { usePlatform } from '@/shared/hooks/useIsNative';
import { useTranslation } from 'react-i18next';
import {
  motion,
  AnimatePresence,
  useMotionValue,
  useTransform,
  useInView,
  animate,
  type AnimationPlaybackControls,
  type Variants,
} from 'framer-motion';
import {
  ChefHat,
  ArrowRight,
  ShoppingBag,
  Globe,
  BadgePercent,
  Eye,
  Heart,
  Target,
  Banknote,
  Lock,
  Receipt,
  Bell,
  Printer,
  Zap,
  CheckCircle2,
  Smartphone,
  Wifi,
  Menu,
  X,
  UserPlus,
  UtensilsCrossed,
  Share2,
  AlertCircle,
  TrendingUp,
  RefreshCw,
  Calendar,
  Search,
  ChevronDown,
} from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { LanguageSwitcher } from '@/shared/components/LanguageSwitcher';
import { SeoHead } from '@/shared/components/SeoHead';
import CookieBanner from '@/features/storefront/components/CookieBanner';

// ─── 3D Tilt Card ────────────────────────────────────────────────────────────

function TiltCard({ children, className }: { children: React.ReactNode; className?: string }) {
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const scale = useMotionValue(1);
  const rotateX = useTransform(y, [-0.5, 0.5], [10, -10]);
  const rotateY = useTransform(x, [-0.5, 0.5], [-10, 10]);

  function onMouseMove(e: React.MouseEvent<HTMLDivElement>) {
    const rect = e.currentTarget.getBoundingClientRect();
    x.set((e.clientX - rect.left) / rect.width - 0.5);
    y.set((e.clientY - rect.top) / rect.height - 0.5);
  }
  function onMouseLeave() {
    x.set(0);
    y.set(0);
    scale.set(1);
  }

  return (
    <motion.div
      style={{ rotateX, rotateY, scale, transformPerspective: 900 }}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      onMouseEnter={() => scale.set(1.02)}
      className={className}
    >
      {children}
    </motion.div>
  );
}

// ─── Animated Counter ────────────────────────────────────────────────────────

function Counter({
  target,
  prefix = '',
  suffix = '',
  locale = 'de-DE',
}: {
  target: number;
  prefix?: string;
  suffix?: string;
  locale?: string;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true });
  const count = useMotionValue(0);
  const display = useTransform(count, (v) =>
    `${prefix}${Math.round(v).toLocaleString(locale)}${suffix}`
  );

  useEffect(() => {
    if (!inView) return;
    const controls: AnimationPlaybackControls = animate(count, target, {
      duration: 2,
      ease: 'easeOut',
    });
    return () => controls.stop();
  }, [inView, count, target]);

  return <motion.span ref={ref}>{display}</motion.span>;
}

// ─── Brand SVGs ──────────────────────────────────────────────────────────────

const GcpLogo = () => (
  <svg viewBox="0 0 48 48" className="w-6 h-6" aria-hidden="true">
    <path fill="#EA4335" d="M29.6 18.4H30L37.2 11.2l.4-3A22.3 22.3 0 0 0 3.7 19.5l2.5-.3 14.6-2.4 1-.4a12.5 12.5 0 0 1 7.8 2Z" />
    <path fill="#4285F4" d="M44.3 19.5a22.3 22.3 0 0 0-6.7-8.3l-7.8 7.8a12.5 12.5 0 0 1 4.6 9.9v1.2a6.3 6.3 0 0 1 0 12.5h-12.5l-1.2 1.3v7.5l1.2 1.2h12.5A16.3 16.3 0 0 0 44.3 19.5Z" />
    <path fill="#34A853" d="M9.7 52.3h12.5v-12.5H9.7a6.2 6.2 0 0 1-2.6-.6l-2.5.8-7.5 7.5-.6 2.5a16.2 16.2 0 0 0 13.2 2.3Z" />
    <path fill="#FBBC05" d="M9.7 20a16.3 16.3 0 0 0-13.2 26.3l8.3-8.3a6.3 6.3 0 1 1 8.3-8.3l8.3-8.3A16.2 16.2 0 0 0 9.7 20Z" />
  </svg>
);

const FirebaseLogo = () => (
  <svg viewBox="0 0 32 32" className="w-5 h-5" aria-hidden="true">
    <path fill="#FFA000" d="M19.62 11.56 17.18 3.06a.5.5 0 0 0-.93-.04l-2.51 5.3 5.88 3.24Z" />
    <path fill="#F57F17" d="m5.08 25 7.42-22.06a.5.5 0 0 1 .94-.01L16 9.66 5.08 25Z" />
    <path fill="#FFCA28" d="M26.92 17.14 23.5 7.5a.5.5 0 0 0-.87-.11l-17.55 17.6 9.83 5.58a2 2 0 0 0 1.98 0l9.84-5.58a1 1 0 0 0 .19-7.85Z" />
  </svg>
);


// ─── Tech Stack Marquee ───────────────────────────────────────────────────────

const TECH_STACK = [
  { label: 'Google Cloud', sublabel: 'europe-west3 · Frankfurt', logo: <GcpLogo /> },
  { label: 'Firebase',     sublabel: 'Auth & Realtime',          logo: <FirebaseLogo /> },
];

function TechMarquee() {
  return (
    <>
      <style>{`
        @keyframes tech-marquee {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
        .tech-marquee-track {
          animation: tech-marquee 30s linear infinite;
        }
        .tech-marquee-track:hover {
          animation-play-state: paused;
        }
      `}</style>
      <div className="relative overflow-hidden">
        <div className="absolute left-0 inset-y-0 w-16 sm:w-28 bg-gradient-to-r from-background to-transparent z-10 pointer-events-none" />
        <div className="absolute right-0 inset-y-0 w-16 sm:w-28 bg-gradient-to-l from-background to-transparent z-10 pointer-events-none" />
        <div className="tech-marquee-track flex gap-3 w-max">
          {Array.from({ length: 8 }, () => TECH_STACK).flat().map((tech, i) => (
            <div
              key={i}
              className="flex items-center gap-3 px-4 py-3 rounded-xl bg-card border border-border shrink-0 hover:border-accent/30 transition-colors cursor-default"
            >
              <div className="shrink-0">{tech.logo}</div>
              <div>
                <p className="text-xs font-semibold text-foreground leading-none">{tech.label}</p>
                {tech.sublabel && <p className="text-[10px] text-muted-foreground mt-0.5">{tech.sublabel}</p>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

// ─── Savings Calculator ───────────────────────────────────────────────────────

const TYPICAL_RATE = 0.30;

function SavingsCalculator() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language?.startsWith('de') ? 'de-DE' : 'en-GB';
  const [monthly, setMonthly] = useState(8000);

  const fmt = (n: number) =>
    n.toLocaleString(locale, { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });

  const saving = monthly * TYPICAL_RATE;
  const yearlySaving = saving * 12;
  const barPct = Math.min((saving / (monthly * 0.35)) * 100, 100);

  const sliderPct = ((Math.min(monthly, 100000) - 500) / (100000 - 500)) * 100;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.55, ease: 'easeOut' }}
      className="bg-card rounded-2xl border border-border shadow-card p-6 sm:p-8"
    >
      {/* Slider row */}
      <div className="mb-8">
        <div className="flex flex-wrap items-end justify-between gap-2 mb-4">
          <label className="text-sm font-medium text-foreground">
            {t('home.calculator.label')}
          </label>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">€</span>
            <input
              type="number"
              min={500}
              max={500000}
              step={500}
              value={monthly}
              onChange={(e) => {
                const v = Math.max(500, Math.min(500000, Number(e.target.value)));
                setMonthly(v);
              }}
              className="w-32 text-right text-xl font-bold text-accent bg-transparent border-b-2 border-accent/40 focus:border-accent outline-none pb-0.5 transition-colors"
            />
          </div>
        </div>

        <input
          type="range"
          min={500}
          max={100000}
          step={500}
          value={Math.min(monthly, 100000)}
          onChange={(e) => setMonthly(Number(e.target.value))}
          className="w-full h-2 rounded-full appearance-none cursor-pointer"
          style={{
            background: `linear-gradient(to right, hsl(25 95% 53%) 0%, hsl(25 95% 53%) ${sliderPct}%, hsl(var(--muted)) ${sliderPct}%, hsl(var(--muted)) 100%)`,
            accentColor: 'hsl(25 95% 53%)',
          }}
        />
        <div className="flex justify-between text-[11px] text-muted-foreground mt-1.5">
          <span>€500</span>
          <span>€100.000</span>
        </div>
      </div>

      {/* Comparison bars */}
      <div className="space-y-5 mb-7">
        {/* Typical delivery platform */}
        <div>
          <div className="flex justify-between items-center text-sm mb-2">
            <span className="font-medium">{t('home.calculator.typicalPlatform')}</span>
            <span className="font-bold text-destructive tabular-nums">
              − {fmt(saving)} / {t('home.calculator.month')}
            </span>
          </div>
          <div className="h-2.5 bg-muted rounded-full overflow-hidden">
            <motion.div
              className="h-full rounded-full bg-destructive/60"
              animate={{ width: `${barPct}%` }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
            />
          </div>
          <p className="text-[11px] text-muted-foreground mt-1">
            {t('home.calculator.typicalRate')} {t('home.calculator.commission')}
          </p>
        </div>

        {/* Restroos */}
        <div>
          <div className="flex justify-between items-center text-sm mb-2">
            <span className="font-bold text-foreground">Restroos</span>
            <span className="font-bold text-success">€0 / {t('home.calculator.month')}</span>
          </div>
          <div className="h-2.5 bg-muted rounded-full overflow-hidden">
            <div className="h-full w-0 rounded-full bg-success" />
          </div>
          <p className="text-[11px] text-muted-foreground mt-1">
            0% {t('home.calculator.commission')}
          </p>
        </div>
      </div>

      {/* Savings result */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <motion.div
          animate={{ scale: [1, 1.02, 1] }}
          transition={{ duration: 0.3 }}
          key={`m-${saving}`}
          className="bg-success/10 border border-success/25 rounded-xl p-4 text-center"
        >
          <p className="text-xs text-muted-foreground mb-1">{t('home.calculator.savePerMonth')}</p>
          <p className="text-2xl font-extrabold text-success tabular-nums">{fmt(saving)}</p>
        </motion.div>
        <motion.div
          animate={{ scale: [1, 1.02, 1] }}
          transition={{ duration: 0.3 }}
          key={`y-${yearlySaving}`}
          className="bg-accent/8 border border-accent/20 rounded-xl p-4 text-center"
        >
          <p className="text-xs text-muted-foreground mb-1">{t('home.calculator.savePerYear')}</p>
          <p className="text-2xl font-extrabold text-accent tabular-nums">{fmt(yearlySaving)}</p>
        </motion.div>
      </div>

      <p className="text-[11px] text-muted-foreground text-center mt-3">
        {t('home.calculator.note')}
      </p>
    </motion.div>
  );
}

// ─── Phone Mockup ─────────────────────────────────────────────────────────────

function PhoneMockup() {
  return (
    <TiltCard className="flex justify-center">
      <div className="relative mx-auto w-[248px] sm:w-[268px] select-none">
        {/* Phone shell */}
        <div className="relative bg-zinc-950 rounded-[50px] p-[10px] shadow-[0_40px_80px_-10px_rgba(0,0,0,0.5),0_0_0_1px_rgba(255,255,255,0.06)]">
          {/* Dynamic island */}
          <div className="absolute top-[14px] left-1/2 -translate-x-1/2 w-[88px] h-[26px] bg-zinc-950 rounded-full z-20" />

          {/* Screen */}
          <div className="relative bg-background rounded-[42px] overflow-hidden" style={{ height: 530 }}>
            {/* Status bar */}
            <div className="px-6 pt-[52px] pb-1 flex justify-between text-[9px] text-foreground/60 font-medium">
              <span>9:41</span>
              <span className="flex items-center gap-1">
                <Wifi className="w-2.5 h-2.5" />
                <span>●●●</span>
              </span>
            </div>

            {/* App header */}
            <div className="px-4 pb-3 border-b border-border flex items-center justify-between">
              <div className="flex items-center gap-2">
                <img src="/logo.svg" alt="Restroos" className="w-7 h-7 rounded-lg shadow-sm shrink-0" />
                <span className="text-xs font-bold">Bestellungen</span>
              </div>
              <motion.div
                animate={{ opacity: [1, 0.3, 1] }}
                transition={{ duration: 1.5, repeat: Infinity }}
                className="flex items-center gap-1 text-[9px] text-success font-semibold"
              >
                <div className="w-1.5 h-1.5 rounded-full bg-success" />
                Live
              </motion.div>
            </div>

            {/* Content */}
            <div className="p-3 space-y-2">
              {/* Incoming order */}
              <motion.div
                initial={{ opacity: 0, y: -16, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ delay: 0.6 }}
                className="bg-accent/10 border border-accent/30 rounded-2xl p-3"
              >
                <div className="flex items-center gap-2 mb-1.5">
                  <div className="w-5 h-5 rounded-full bg-accent flex items-center justify-center">
                    <Bell className="w-2.5 h-2.5 text-white" />
                  </div>
                  <span className="text-[10px] font-bold text-accent">Neue Bestellung!</span>
                  <span className="text-[9px] text-muted-foreground ml-auto">jetzt</span>
                </div>
                <div className="flex justify-between items-end">
                  <div>
                    <p className="text-xs font-bold">#1042</p>
                    <p className="text-[9px] text-muted-foreground">2× Pizza · 1× Pasta</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs font-extrabold text-accent">€37,00</p>
                    <p className="text-[9px] text-muted-foreground">Lieferung</p>
                  </div>
                </div>
              </motion.div>

              {[
                { id: '#1041', item: '1× Burger Meal', price: '€24,50', status: 'In Arbeit', color: 'bg-amber-500/10 text-amber-600 border-amber-500/20' },
                { id: '#1040', item: '3× Sushi Set',   price: '€52,00', status: 'Fertig',   color: 'bg-success/10 text-success border-success/20' },
              ].map((order, i) => (
                <motion.div
                  key={order.id}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.9 + i * 0.15 }}
                  className="bg-card border border-border rounded-2xl p-3"
                >
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="text-[10px] font-bold">{order.id}</span>
                    <span className="text-xs font-extrabold">{order.price}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[9px] text-muted-foreground">{order.item}</span>
                    <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full border ${order.color}`}>{order.status}</span>
                  </div>
                </motion.div>
              ))}

              {/* Daily summary */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.3 }}
                className="bg-success/8 border border-success/20 rounded-2xl p-3 flex justify-between items-center"
              >
                <div>
                  <p className="text-[9px] text-muted-foreground font-medium">Provision heute</p>
                  <p className="text-base font-extrabold text-success">€0,00</p>
                </div>
                <div className="text-right">
                  <p className="text-[9px] text-muted-foreground font-medium">Umsatz</p>
                  <p className="text-base font-extrabold text-foreground">€2.000,00</p>
                </div>
              </motion.div>

              {/* Auto-print indicator */}
              <motion.div
                animate={{ opacity: [0.6, 1, 0.6] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="flex items-center gap-2 text-[9px] text-muted-foreground bg-muted/50 rounded-xl px-3 py-2"
              >
                <Printer className="w-3 h-3 text-accent shrink-0" />
                <span>Bon wird automatisch gedruckt…</span>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Physical buttons */}
        <div className="absolute right-[-2px] top-24 w-[3px] h-10 bg-zinc-800 rounded-l-full" />
        <div className="absolute left-[-2px] top-20 w-[3px] h-6 bg-zinc-800 rounded-r-full" />
        <div className="absolute left-[-2px] top-28 w-[3px] h-14 bg-zinc-800 rounded-r-full" />
        <div className="absolute left-[-2px] top-44 w-[3px] h-14 bg-zinc-800 rounded-r-full" />

        {/* Floating badge */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.5 }}
          className="absolute -bottom-3 -left-4 bg-card border border-border text-xs font-semibold px-3 py-1.5 rounded-full shadow-card flex items-center gap-1.5"
        >
          <Smartphone className="w-3 h-3 text-accent" />
          Benachrichtigung gesendet
        </motion.div>
      </div>
    </TiltCard>
  );
}

// ─── Desktop Mockup — Orders Page ────────────────────────────────────────────

const BOARD_COLUMNS_MOCK = [
  {
    key: 'PENDING', label: 'PENDING', count: 2,
    tone: 'text-destructive', bg: 'bg-destructive/5', border: 'border-destructive/20',
    orders: [
      { id: '#042', name: 'Thomas M.', total: '€37,00', type: 'Delivery', borderTone: 'border-l-destructive/60', action: 'Accept', actionCls: 'bg-accent text-white', items: '2× Pizza · 1× Pasta' },
      { id: '#039', name: 'Anna B.',   total: '€19,50', type: 'Pickup',   borderTone: 'border-l-destructive/60', action: 'Accept', actionCls: 'bg-accent text-white', items: '1× Burger Meal' },
    ],
  },
  {
    key: 'PREPARING', label: 'PREPARING', count: 1,
    tone: 'text-accent', bg: 'bg-accent/5', border: 'border-accent/20',
    orders: [
      { id: '#041', name: 'Maria K.',  total: '€52,00', type: 'Pickup',   borderTone: 'border-l-accent/60',       action: 'Ready',  actionCls: 'bg-success/15 text-success', items: '3× Sushi Set' },
    ],
  },
  {
    key: 'DONE', label: 'DONE', count: 7,
    tone: 'text-success', bg: 'bg-success/5', border: 'border-success/20',
    orders: [
      { id: '#040', name: 'Sarah L.',  total: '€24,50', type: 'Delivery', borderTone: 'border-l-border',          action: null,     actionCls: '', items: '1× Salat · 2× Getränke' },
    ],
  },
] as const;

function DesktopMockup() {
  return (
    <TiltCard>
      <div
        className="w-full max-w-[560px] select-none"
        style={{ filter: 'drop-shadow(0 30px 60px rgba(0,0,0,0.18))' }}
      >
        {/* Browser chrome */}
        <div className="bg-zinc-900 rounded-t-xl px-3 py-2.5 flex items-center gap-2.5 border border-zinc-800 border-b-0">
          <div className="flex gap-1.5 shrink-0">
            <div className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
            <div className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
            <div className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
          </div>
          <div className="flex-1 bg-zinc-800 rounded-md px-3 py-1 text-[9px] text-zinc-400 font-mono text-center truncate">
            app.restaurantos.de/orders
          </div>
          <div className="w-14 shrink-0" />
        </div>

        {/* App window */}
        <div className="bg-background border border-zinc-800 rounded-b-xl flex overflow-hidden" style={{ height: 330 }}>

          {/* Left sidebar */}
          <div className="w-12 bg-card border-r border-border flex flex-col items-center py-3 gap-2.5 shrink-0">
            <img src="/logo.svg" alt="Restroos" className="w-7 h-7 rounded-xl shrink-0" />
            {[
              { Icon: ShoppingBag, active: true },
              { Icon: Globe,       active: false },
              { Icon: Receipt,     active: false },
            ].map(({ Icon, active }, i) => (
              <div key={i} className={`w-8 h-8 rounded-lg flex items-center justify-center ${active ? 'bg-accent/15' : ''}`}>
                <Icon className={`w-4 h-4 ${active ? 'text-accent' : 'text-muted-foreground/35'}`} />
              </div>
            ))}
          </div>

          {/* Page content */}
          <div className="flex-1 flex flex-col min-w-0 overflow-hidden">

            {/* Page header */}
            <div className="flex items-center justify-between px-3 py-2.5 border-b border-border shrink-0">
              <div className="flex items-center gap-2">
                <ShoppingBag className="w-4 h-4 text-accent shrink-0" />
                <span className="text-sm font-bold">Orders</span>
                <motion.div
                  animate={{ opacity: [1, 0.4, 1] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                  className="flex items-center gap-1 text-[9px] text-success font-semibold"
                >
                  <div className="w-1.5 h-1.5 rounded-full bg-success" />
                  Live
                </motion.div>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-7 h-7 rounded-md border border-border bg-card flex items-center justify-center">
                  <RefreshCw className="w-3 h-3 text-muted-foreground" />
                </div>
                <div className="flex items-center gap-1 px-2 h-7 rounded-md border border-border bg-card text-[9px] text-muted-foreground">
                  <Calendar className="w-2.5 h-2.5 shrink-0" />
                  <span>Today</span>
                </div>
              </div>
            </div>

            {/* Stats bar — mirrors OrdersStatsBar */}
            <div className="grid grid-cols-4 gap-1.5 px-2.5 py-2 border-b border-border shrink-0">
              {([
                { Icon: AlertCircle, label: '2 pending',  sub: 'needs action', tone: 'text-destructive', bg: 'bg-destructive/10', pulse: true  },
                { Icon: ChefHat,     label: '3 active',   sub: 'in kitchen',   tone: 'text-accent',      bg: 'bg-accent/10',      pulse: false },
                { Icon: Receipt,     label: '24 today',   sub: 'all accepted', tone: 'text-primary',     bg: 'bg-primary/10',     pulse: false },
                { Icon: TrendingUp,  label: '€2.000',     sub: 'avg €83',      tone: 'text-success',     bg: 'bg-success/10',     pulse: false },
              ] as const).map((s, i) => (
                <div key={i} className="flex items-center gap-1.5 px-2 py-1.5 bg-card border border-border rounded-lg overflow-hidden">
                  <div className={`relative w-5 h-5 rounded flex items-center justify-center shrink-0 ${s.bg} ${s.tone}`}>
                    <s.Icon className="w-2.5 h-2.5" />
                    {s.pulse && <span className={`absolute inset-0 rounded animate-ping opacity-40 ${s.bg}`} />}
                  </div>
                  <div className="min-w-0">
                    <p className="text-[9px] font-semibold leading-none truncate">{s.label}</p>
                    <p className="text-[8px] text-muted-foreground leading-none mt-0.5 truncate">{s.sub}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Filters — mirrors OrdersFilters */}
            <div className="flex items-center gap-1.5 px-2.5 py-1.5 border-b border-border shrink-0">
              <div className="flex-1 flex items-center gap-1.5 px-2 py-1 rounded-md border border-border bg-muted/30 text-[9px] text-muted-foreground">
                <Search className="w-2.5 h-2.5 shrink-0" />
                <span>Search orders…</span>
              </div>
              {['All', 'Newest'].map((lbl) => (
                <div key={lbl} className="flex items-center gap-0.5 px-1.5 py-1 rounded-md border border-border bg-card text-[9px] text-muted-foreground">
                  {lbl} <ChevronDown className="w-2.5 h-2.5 shrink-0" />
                </div>
              ))}
            </div>

            {/* Kanban board — mirrors OrdersBoard columns */}
            <div className="flex gap-1.5 p-1.5 flex-1 min-h-0 overflow-hidden">
              {BOARD_COLUMNS_MOCK.map((col, ci) => (
                <div key={col.key} className={`flex-1 min-w-0 flex flex-col rounded-lg border ${col.bg} ${col.border} overflow-hidden`}>
                  {/* Column header */}
                  <div className={`flex items-center gap-1.5 px-2 py-1.5 border-b ${col.border} shrink-0`}>
                    <span className={`text-[8px] font-bold uppercase tracking-wide ${col.tone}`}>{col.label}</span>
                    <span className="text-[8px] font-bold bg-secondary rounded px-1.5 leading-relaxed">{col.count}</span>
                  </div>
                  {/* Cards */}
                  <div className="p-1 space-y-1 overflow-hidden">
                    {col.orders.map((order, oi) => (
                      <motion.div
                        key={order.id}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.55 + ci * 0.12 + oi * 0.1 }}
                        className={`bg-background border-l-[3px] border border-border/60 rounded-md p-1.5 ${order.borderTone}`}
                      >
                        <div className="flex items-center justify-between mb-0.5">
                          <span className="text-[8px] font-mono font-bold bg-muted px-1 rounded">{order.id}</span>
                          <span className="text-[8px] font-bold tabular-nums">{order.total}</span>
                        </div>
                        <p className="text-[9px] font-medium truncate">{order.name}</p>
                        <p className="text-[7px] text-muted-foreground truncate mt-0.5">{order.items}</p>
                        <p className="text-[7px] text-muted-foreground mt-0.5">{order.type}</p>
                        {order.action && (
                          <div className={`mt-1 w-full text-center text-[8px] font-bold rounded py-0.5 ${order.actionCls}`}>
                            {order.action}
                          </div>
                        )}
                      </motion.div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

          </div>
        </div>
      </div>
    </TiltCard>
  );
}

// ─── WhatsApp Contact Float ───────────────────────────────────────────────────

const WA_LABELS = ['Register your restaurant', 'Restaurant registrieren'] as const;
const WA_HREF = `https://wa.me/4917623835765?text=${encodeURIComponent('Hallo, ich möchte mein Restaurant bei Restroos registrieren.')}`;

function WhatsAppContactFloat() {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setIdx((i) => (i + 1) % WA_LABELS.length), 2800);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="fixed bottom-6 right-4 z-50 flex items-center gap-2.5 select-none pointer-events-none">
      {/* Alternating label */}
      <AnimatePresence mode="wait">
        <motion.div
          key={idx}
          initial={{ opacity: 0, x: 10 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -10 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          className="pointer-events-none bg-card border border-border shadow-lg rounded-xl px-3 py-2 text-xs font-semibold text-foreground whitespace-nowrap"
        >
          {WA_LABELS[idx]}
        </motion.div>
      </AnimatePresence>

      {/* Button */}
      <a
        href={WA_HREF}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Contact on WhatsApp to register"
        className="relative flex w-14 h-14 rounded-full items-center justify-center pointer-events-auto"
      >
        <span className="absolute inset-0 rounded-full bg-[#25D366] opacity-30 animate-ping" aria-hidden="true" />
        <span className="relative flex w-14 h-14 rounded-full bg-[#25D366] shadow-lg shadow-green-500/40 items-center justify-center hover:scale-110 transition-transform duration-150 active:scale-95">
          <svg viewBox="0 0 24 24" className="w-7 h-7 fill-white" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
          </svg>
        </span>
      </a>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

const fadeUp: Variants = {
  hidden: { opacity: 0, y: 32 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: 'easeOut' } },
};

const stagger: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.1 } },
};

const STEPS = [
  { icon: UserPlus,        num: '01', titleKey: 'home.steps.register.title', descKey: 'home.steps.register.description' },
  { icon: UtensilsCrossed, num: '02', titleKey: 'home.steps.menu.title',     descKey: 'home.steps.menu.description' },
  { icon: Share2,          num: '03', titleKey: 'home.steps.share.title',    descKey: 'home.steps.share.description' },
  { icon: Bell,            num: '04', titleKey: 'home.steps.notify.title',   descKey: 'home.steps.notify.description' },
  { icon: Banknote,        num: '05', titleKey: 'home.steps.earn.title',     descKey: 'home.steps.earn.description' },
] as const;

const Index = () => {
  const platform = usePlatform();
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);

  if (platform === 'ios' || platform === 'android') {
    return <Navigate to="/login" replace />;
  }

  const features = [
    {
      icon: ShoppingBag,
      title: t('home.features.orderManagement.title'),
      description: t('home.features.orderManagement.description'),
    },
    {
      icon: Globe,
      title: t('home.features.publicWebsites.title'),
      description: t('home.features.publicWebsites.description'),
    },
    {
      icon: BadgePercent,
      title: t('home.features.zeroCommission.title'),
      description: t('home.features.zeroCommission.description'),
    },
  ];

  const integrations = [
    {
      icon: Bell,
      badge: t('home.integrations.notifications.badge'),
      title: t('home.integrations.notifications.title'),
      description: t('home.integrations.notifications.description'),
      steps: [
        t('home.integrations.notifications.step1'),
        t('home.integrations.notifications.step2'),
        t('home.integrations.notifications.step3'),
      ],
      color: 'from-blue-500/10 to-blue-600/5',
      iconColor: 'text-blue-500',
      iconBg: 'bg-blue-500/10',
    },
    {
      icon: Printer,
      badge: t('home.integrations.printer.badge'),
      title: t('home.integrations.printer.title'),
      description: t('home.integrations.printer.description'),
      steps: [
        t('home.integrations.printer.step1'),
        t('home.integrations.printer.step2'),
        t('home.integrations.printer.step3'),
      ],
      color: 'from-accent/10 to-accent/5',
      iconColor: 'text-accent',
      iconBg: 'bg-accent/10',
    },
  ];

  const values = [
    {
      icon: Banknote,
      title: t('home.values.zeroCommission.title'),
      description: t('home.values.zeroCommission.description'),
    },
    {
      icon: Lock,
      title: t('home.values.ownership.title'),
      description: t('home.values.ownership.description'),
    },
    {
      icon: Receipt,
      title: t('home.values.transparency.title'),
      description: t('home.values.transparency.description'),
    },
  ];

  return (
    <div className="min-h-screen bg-background overflow-x-hidden safe-area-bottom">
      <SeoHead />

      {/* ── Navigation ─────────────────────────────────────────────────────── */}
      <div className="sticky top-0 z-50">
        <motion.nav
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="border-b border-border bg-card/80 backdrop-blur-xl safe-area-top"
        >
          <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <img src="/logo.svg" alt="restroos" className="w-9 h-9 rounded-xl shrink-0" />
              <span className="font-bold text-lg tracking-tight">
                <span className="text-foreground">Res</span><span className="text-accent">troos</span>
              </span>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <LanguageSwitcher />
              {/* Desktop links */}
              <Button variant="ghost" size="sm" className="hidden md:inline-flex" asChild>
                <Link to="/login" target="_blank" rel="noopener noreferrer">{t('home.nav.signIn')}</Link>
              </Button>
              <Button className="btn-accent gap-1.5 text-sm hidden md:inline-flex" size="sm" asChild>
                <Link to="/register">
                  {t('home.nav.getStarted')}
                  <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </Button>
              {/* Hamburger */}
              <button
                onClick={() => setMenuOpen((o) => !o)}
                className="md:hidden p-2 rounded-lg hover:bg-accent/10 transition-colors"
                aria-label="Toggle menu"
              >
                {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </motion.nav>

        {/* Mobile dropdown menu */}
        <AnimatePresence>
          {menuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.22, ease: 'easeOut' }}
              className="md:hidden border-b border-border bg-card/95 backdrop-blur-xl overflow-hidden"
            >
              <div className="max-w-6xl mx-auto px-4 py-5 flex flex-col gap-1">
                {/* Primary CTAs */}
                <Link
                  to="/login"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setMenuOpen(false)}
                  className="text-sm font-medium py-2.5 px-3 rounded-xl hover:bg-accent/10 transition-colors"
                >
                  {t('home.nav.signIn')}
                </Link>
                <Button className="btn-accent gap-2 justify-center mb-3" asChild>
                  <Link to="/register" onClick={() => setMenuOpen(false)}>
                    {t('home.nav.getStarted')}
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </Button>

                {/* Platform links */}
                <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground px-3 mb-1">
                  {t('home.nav.platform')}
                </p>
                {[
                  { label: t('home.nav.features'),    href: '#features' },
                  { label: t('home.nav.howItWorks'),  href: '#how-it-works' },
                  { label: t('home.nav.calculator'),  href: '#calculator' },
                ].map(({ label, href }) => (
                  <a
                    key={href}
                    href={href}
                    onClick={() => setMenuOpen(false)}
                    className="text-sm text-muted-foreground py-2 px-3 rounded-xl hover:bg-accent/10 hover:text-foreground transition-colors"
                  >
                    {label}
                  </a>
                ))}

                {/* Legal links */}
                <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground px-3 mt-3 mb-1">
                  {t('home.nav.legal')}
                </p>
                <div className="flex flex-wrap gap-x-1 gap-y-0.5 px-3">
                  {[
                    { label: 'Impressum',  to: '/legal/impressum' },
                    { label: 'Datenschutz', to: '/legal/datenschutz' },
                    { label: 'AGB',        to: '/legal/agb' },
                  ].map(({ label, to }) => (
                    <Link
                      key={to}
                      to={to}
                      onClick={() => setMenuOpen(false)}
                      className="text-xs text-muted-foreground py-1.5 px-2 rounded-lg hover:bg-accent/10 hover:text-foreground transition-colors"
                    >
                      {label}
                    </Link>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* ── Hero ───────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden min-h-[90vh] flex items-center">
        {/* Animated background orbs */}
        <div className="absolute inset-0 pointer-events-none">
          <motion.div
            className="absolute -top-32 -left-32 w-[500px] h-[500px] rounded-full bg-accent/8 blur-3xl"
            animate={{ x: [0, 40, 0], y: [0, -30, 0], scale: [1, 1.15, 1] }}
            transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.div
            className="absolute -bottom-32 -right-32 w-[420px] h-[420px] rounded-full bg-accent/12 blur-3xl"
            animate={{ x: [0, -30, 0], y: [0, 40, 0], scale: [1, 1.2, 1] }}
            transition={{ duration: 11, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
          />
          <motion.div
            className="absolute top-1/3 right-1/4 w-64 h-64 rounded-full bg-primary/4 blur-3xl"
            animate={{ scale: [1, 1.3, 1], opacity: [0.4, 0.7, 0.4] }}
            transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
          />
        </div>

        <div className="relative max-w-6xl mx-auto px-4 py-20 md:py-28 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center">

            {/* Left — copy */}
            <motion.div
              variants={stagger}
              initial="hidden"
              animate="show"
              className="text-left"
            >
              {/* Badge */}
              <motion.div variants={fadeUp} className="inline-flex items-center gap-2 bg-accent/10 text-accent border border-accent/20 text-xs font-semibold px-3 py-1.5 rounded-full mb-6">
                <Zap className="w-3 h-3" />
                {t('home.hero.badge')}
              </motion.div>

              <motion.h1
                variants={fadeUp}
                className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] mb-5"
              >
                {t('home.hero.title')}{' '}
                <span className="bg-gradient-to-r from-accent to-orange-600 bg-clip-text text-transparent">
                  {t('home.hero.titleAccent')}
                </span>
              </motion.h1>

              <motion.p
                variants={fadeUp}
                className="text-base sm:text-lg text-muted-foreground leading-7 mb-8 max-w-xl"
              >
                {t('home.hero.subtitle')}
              </motion.p>

              <motion.div variants={fadeUp} className="flex flex-wrap gap-3">
                <Button size="lg" className="btn-accent gap-2 h-12 px-8 text-base" asChild>
                  <Link to="/register">
                    {t('home.hero.startFree')}
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </Button>
                <Button size="lg" variant="outline" className="h-12 px-8 text-base" asChild>
                  <Link to="/rasam">{t('home.hero.viewDemo')}</Link>
                </Button>
              </motion.div>

              {/* Pricing pill */}
              <motion.div variants={fadeUp} className="flex items-center gap-2 mt-1">
                <span className="inline-flex items-center gap-1.5 bg-success/8 border border-success/20 text-success text-xs font-semibold px-3 py-1.5 rounded-full">
                  <Banknote className="w-3 h-3" />
                  {t('home.hero.pricing')}
                </span>
              </motion.div>

              {/* Trust row */}
              <motion.div
                variants={fadeUp}
                className="flex flex-wrap items-center gap-4 mt-8 text-xs text-muted-foreground"
              >
                {[
                  t('home.hero.trust1'),
                  t('home.hero.trust2'),
                  t('home.hero.trust3'),
                ].map((item) => (
                  <div key={item} className="flex items-center gap-1.5">
                    <CheckCircle2 className="w-3.5 h-3.5 text-success" />
                    <span>{item}</span>
                  </div>
                ))}
              </motion.div>
            </motion.div>

            {/* Right — mockups */}

            {/* Mobile: phone only */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.3, ease: 'easeOut' }}
              className="lg:hidden flex justify-center"
            >
              <PhoneMockup />
            </motion.div>

            {/* Desktop: orders-page board as primary, phone as scaled overlay */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.3, ease: 'easeOut' }}
              className="hidden lg:block relative"
            >
              <DesktopMockup />
              <motion.div
                initial={{ opacity: 0, x: -10, y: 10 }}
                animate={{ opacity: 1, x: 0, y: 0 }}
                transition={{ duration: 0.7, delay: 0.6, ease: 'easeOut' }}
                className="absolute -bottom-16 -left-8 z-10"
                style={{ transform: 'scale(0.62)', transformOrigin: 'bottom left' }}
              >
                <PhoneMockup />
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── How it works ────────────────────────────────────────────────────── */}
      <section id="how-it-works" className="py-16 sm:py-24 bg-secondary/30">
        <div className="max-w-4xl mx-auto px-4">
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: '-60px' }}
            className="text-center mb-14"
          >
            <motion.div variants={fadeUp} className="inline-flex items-center gap-2 bg-accent/10 text-accent border border-accent/20 text-xs font-semibold px-3 py-1.5 rounded-full mb-4">
              <Zap className="w-3 h-3" />
              {t('home.steps.badge')}
            </motion.div>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold mb-3">
              {t('home.steps.title')}
            </motion.h2>
            <motion.p variants={fadeUp} className="text-muted-foreground max-w-md mx-auto">
              {t('home.steps.subtitle')}
            </motion.p>
          </motion.div>

          {/* Vertical stepper */}
          <div className="relative max-w-2xl mx-auto">
            {/* Connecting line */}
            <div className="absolute left-[19px] top-5 bottom-5 w-px bg-gradient-to-b from-accent/40 via-border to-transparent hidden sm:block" />

            <div className="space-y-8 sm:space-y-10">
              {STEPS.map(({ icon: Icon, num, titleKey, descKey }, i) => (
                <motion.div
                  key={num}
                  initial={{ opacity: 0, x: -24 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: '-40px' }}
                  transition={{ duration: 0.5, delay: i * 0.1, ease: 'easeOut' }}
                  className="relative flex gap-5 sm:gap-7"
                >
                  {/* Step circle */}
                  <div className="relative shrink-0 z-10">
                    <div className="w-10 h-10 rounded-full bg-accent text-white flex items-center justify-center text-xs font-extrabold shadow-md ring-4 ring-background">
                      {num}
                    </div>
                  </div>

                  {/* Card */}
                  <div className="flex-1 pb-2">
                    <TiltCard>
                      <div className="bg-card border border-border rounded-2xl p-5 hover:border-accent/25 transition-colors">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-8 h-8 rounded-xl bg-accent/10 flex items-center justify-center shrink-0">
                            <Icon className="w-4 h-4 text-accent" />
                          </div>
                          <h3 className="font-semibold text-base">{t(titleKey)}</h3>
                        </div>
                        <p className="text-sm text-muted-foreground leading-relaxed">{t(descKey)}</p>
                      </div>
                    </TiltCard>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Savings Calculator ──────────────────────────────────────────────── */}
      <section id="calculator" className="py-16 sm:py-20">
        <div className="max-w-4xl mx-auto px-4">
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: '-60px' }}
            className="text-center mb-10"
          >
            <motion.div variants={fadeUp} className="inline-flex items-center gap-2 bg-success/10 text-success border border-success/20 text-xs font-semibold px-3 py-1.5 rounded-full mb-4">
              <Banknote className="w-3 h-3" />
              {t('home.calculator.badge')}
            </motion.div>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold mb-3">
              {t('home.calculator.title')}
            </motion.h2>
            <motion.p variants={fadeUp} className="text-muted-foreground max-w-lg mx-auto">
              {t('home.calculator.subtitle')}
            </motion.p>
          </motion.div>

          <SavingsCalculator />
        </div>
      </section>

      {/* ── Features ────────────────────────────────────────────────────────── */}
      <section id="features" className="py-16 sm:py-20">
        <div className="max-w-6xl mx-auto px-4">
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: '-60px' }}
            className="text-center mb-12"
          >
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold mb-3">
              {t('home.features.title')}
            </motion.h2>
            <motion.p variants={fadeUp} className="text-muted-foreground">
              {t('home.features.subtitle')}
            </motion.p>
          </motion.div>

          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="grid grid-cols-1 sm:grid-cols-3 gap-5"
          >
            {features.map((feature) => {
              const Icon = feature.icon;
              return (
                <motion.div key={feature.title} variants={fadeUp}>
                  <TiltCard className="h-full">
                    <div className="h-full bg-card rounded-2xl p-6 border border-border shadow-card hover:border-accent/20 transition-colors">
                      <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center mb-4">
                        <Icon className="w-6 h-6 text-accent" />
                      </div>
                      <h3 className="font-semibold text-base mb-2">{feature.title}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">{feature.description}</p>
                    </div>
                  </TiltCard>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* ── Integrations ────────────────────────────────────────────────────── */}
      <section className="py-16 sm:py-20 bg-secondary/30">
        <div className="max-w-6xl mx-auto px-4">
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: '-60px' }}
            className="text-center mb-12"
          >
            <motion.div variants={fadeUp} className="inline-flex items-center gap-2 bg-accent/10 text-accent border border-accent/20 text-xs font-semibold px-3 py-1.5 rounded-full mb-4">
              <Wifi className="w-3 h-3" />
              {t('home.integrations.badge')}
            </motion.div>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold mb-3">
              {t('home.integrations.title')}
            </motion.h2>
            <motion.p variants={fadeUp} className="text-muted-foreground max-w-xl mx-auto">
              {t('home.integrations.subtitle')}
            </motion.p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {integrations.map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.55, delay: index * 0.1, ease: [0.16, 1, 0.3, 1] }}
                >
                  <TiltCard className="h-full">
                    <div className={`h-full bg-gradient-to-br ${item.color} bg-card rounded-2xl border border-border p-7 relative overflow-hidden`}>
                      <div className={`w-12 h-12 rounded-xl ${item.iconBg} flex items-center justify-center mb-5`}>
                        <Icon className={`w-6 h-6 ${item.iconColor}`} />
                      </div>
                      <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{item.badge}</span>
                      <h3 className="text-xl font-bold mt-1 mb-3">{item.title}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed mb-6">{item.description}</p>

                      {/* Steps */}
                      <div className="space-y-3">
                        {item.steps.map((step, i) => (
                          <motion.div
                            key={step}
                            initial={{ opacity: 0, x: -10 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.2 + i * 0.1 }}
                            className="flex items-center gap-3 text-sm"
                          >
                            <div className="w-5 h-5 rounded-full bg-accent/20 text-accent text-[10px] font-bold flex items-center justify-center shrink-0">
                              {i + 1}
                            </div>
                            <span className="text-muted-foreground">{step}</span>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  </TiltCard>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Mission + Vision ────────────────────────────────────────────────── */}
      <section className="py-16 sm:py-20">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            {[
              { icon: Target, titleKey: 'home.mission.title', bodyKey: 'home.mission.body' },
              { icon: Eye, titleKey: 'home.vision.title', bodyKey: 'home.vision.body' },
            ].map(({ icon: Icon, titleKey, bodyKey }, i) => (
              <motion.div
                key={titleKey}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
              >
                <TiltCard className="h-full">
                  <div className="h-full bg-card border border-border rounded-2xl p-7">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center">
                        <Icon className="w-5 h-5 text-accent" />
                      </div>
                      <h2 className="text-lg font-bold">{t(titleKey)}</h2>
                    </div>
                    <p className="text-sm text-muted-foreground leading-7">{t(bodyKey)}</p>
                  </div>
                </TiltCard>
              </motion.div>
            ))}
          </div>

          {/* Values */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-8"
          >
            <div className="flex items-center justify-center gap-2 mb-1">
              <Heart className="w-5 h-5 text-accent" />
              <h2 className="text-2xl font-bold">{t('home.values.title')}</h2>
            </div>
            <p className="text-sm text-muted-foreground">{t('home.values.subtitle')}</p>
          </motion.div>

          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="grid grid-cols-1 sm:grid-cols-3 gap-5"
          >
            {values.map((value) => {
              const Icon = value.icon;
              return (
                <motion.div key={value.title} variants={fadeUp}>
                  <TiltCard className="h-full">
                    <div className="h-full bg-accent/5 border border-accent/15 rounded-2xl p-6 text-center">
                      <div className="w-11 h-11 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-3">
                        <Icon className="w-5 h-5 text-accent" />
                      </div>
                      <h3 className="font-semibold text-sm mb-2">{value.title}</h3>
                      <p className="text-xs text-muted-foreground leading-relaxed">{value.description}</p>
                    </div>
                  </TiltCard>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* ── Powered by ──────────────────────────────────────────────────────── */}
      <section className="py-10 sm:py-14 border-t border-border overflow-hidden">
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-7"
        >
          {t('home.poweredBy.title')}
        </motion.p>
        <TechMarquee />
      </section>

      {/* ── CTA ─────────────────────────────────────────────────────────────── */}
      <section className="py-20 sm:py-24 relative overflow-hidden">
        <div className="absolute inset-0 gradient-warm opacity-95 pointer-events-none" />
        <div className="absolute inset-0 pointer-events-none">
          <motion.div
            className="absolute -top-20 -left-20 w-80 h-80 rounded-full bg-white/10 blur-3xl"
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ duration: 8, repeat: Infinity }}
          />
          <motion.div
            className="absolute -bottom-20 -right-20 w-64 h-64 rounded-full bg-white/10 blur-3xl"
            animate={{ scale: [1, 1.3, 1] }}
            transition={{ duration: 10, repeat: Infinity, delay: 2 }}
          />
        </div>
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="relative max-w-3xl mx-auto px-4 text-center"
        >
          <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-5 leading-tight">
            {t('home.cta.title')}
          </motion.h2>
          <motion.p variants={fadeUp} className="text-white/80 text-base sm:text-lg mb-8 max-w-xl mx-auto">
            {t('home.cta.subtitle')}
          </motion.p>
          <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button size="lg" className="bg-white text-accent hover:bg-white/90 font-bold gap-2 h-13 px-8 text-base shadow-lg" asChild>
              <Link to="/register">
                {t('home.cta.button')}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </Button>
            <Button size="lg" variant="outline" className="border-white/40 text-white bg-transparent hover:bg-white/10 h-12 px-8 text-base" asChild>
              <Link to="/rasam">{t('home.hero.viewDemo')}</Link>
            </Button>
          </motion.div>
          <motion.p variants={fadeUp} className="text-white/60 text-xs mt-5">
            {t('home.cta.note')}
          </motion.p>
        </motion.div>
      </section>

      {/* ── Footer ──────────────────────────────────────────────────────────── */}
      <footer className="border-t border-border py-8">
        <div className="max-w-6xl mx-auto px-4 text-center text-xs text-muted-foreground space-y-3">
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
            <Link to="/legal/impressum" className="hover:text-foreground transition-colors">Impressum</Link>
            <span aria-hidden="true">·</span>
            <Link to="/legal/datenschutz" className="hover:text-foreground transition-colors">Datenschutz</Link>
            <span aria-hidden="true">·</span>
            <Link to="/legal/agb" className="hover:text-foreground transition-colors">AGB</Link>
          </div>
          <p>{t('home.footer.copyright')}</p>
          <p className="text-[10px]">{t('home.footer.trademark')}</p>
        </div>
      </footer>

      {/* WhatsApp registration contact */}
      <WhatsAppContactFloat />

      {/* Cookie banner */}
      <CookieBanner />
    </div>
  );
};

export default Index;
